import React from "react";
import { Box, type SxProps } from "@mui/material";
import CustomButton from "../Buttons/Button/CustomButton";

export interface FixedActionBarProps {
  submitLabel?: string;
  isLoading?: boolean;
  disabled?: boolean;
  showCancel?: boolean;
  cancelLabel?: string;
  onCancel?: () => void;
  cancelButtonCategory?: "cancel" | "delete" | "search" | "clear";
  /**
   * PreviewDrawer など transform を持つ親の中に埋め込む場合 true。
   * position: sticky でスクロール末尾に追従させる。
   */
  embedded?: boolean;
  /**
   * 指定された場合、submit ボタンは type="button" として扱われ、
   * クリック時にこのハンドラが呼ばれる (フォーム外配置や複数フォーム送信に利用)。
   */
  onSubmit?: () => void;
  sx?: SxProps;
}

const FixedActionBar: React.FC<FixedActionBarProps> = ({
  submitLabel = "更新",
  isLoading = false,
  disabled = false,
  showCancel = false,
  cancelLabel = "キャンセル",
  onCancel,
  cancelButtonCategory = "cancel",
  embedded = false,
  onSubmit,
  sx,
}) => {
  return (
    <Box
      sx={{
        // デザイン
        background: "#F7FBFD",
        display: "flex",
        justifyContent: "center",
        gap: "10px",
        padding: "10px 0",

        // 位置: embedded=false → viewport 固定、true → drawer の下端に追従。
        // embedded 時も position:fixed を使うのが要点。PreviewDrawer は transform を
        // 持つので fixed の containing block が drawer になり、かつ fixed は途中の
        // position:relative 祖先 (例: PropertyRegistrationTabs のカード Box) を
        // 無視するため、間に positioned 要素があっても drawer 端まで届く
        // (absolute だと最も近い positioned 祖先に張り付いてしまい左右に余白が出る)。
        ...(embedded
          ? {
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
            }
          : {
              position: "fixed",
              bottom: 0,
              width: "calc(100vw - var(--sidebar-width, 210px) - 1px)",
              left: "calc(var(--sidebar-width, 210px) + 1px)",
            }),
        zIndex: 1000,

        // カスタムスタイル
        ...sx,
      }}
    >
      {/* キャンセル/戻るボタン（オプション） */}
      {showCancel && (
        <CustomButton
          sx={{ width: { lg: "100px", xs: "80px" } }}
          label={cancelLabel}
          buttonCategory={cancelButtonCategory}
          onClick={onCancel}
          type="button"
        />
      )}

      {/* 送信ボタン */}
      <CustomButton
        sx={{ width: { lg: "100px", xs: "80px" } }}
        label={isLoading ? `${submitLabel}中...` : submitLabel}
        type={onSubmit ? "button" : "submit"}
        onClick={onSubmit}
        disabled={disabled}
        isLoading={isLoading}
      />
    </Box>
  );
};

export default FixedActionBar;
